'use client';

import { usePathname } from 'next/navigation';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import { useChatMode } from './ChatModeContext';
import { useWorkspace } from './WorkspaceContext';
import WorkspaceSetting from '@/components/Workspace/WorkspaceSetting';
import { useTranslation } from '@/lib/i18n';

interface HeaderContextType {
  title: string | ReactNode;
  setTitle: (title: string | ReactNode) => void;
  isTransparent: boolean;
  setIsTransparent: (isTransparent: boolean) => void;
  rightContent: ReactNode | null;
  setRightContent: (content: ReactNode | null) => void;
  showBorder: boolean;
  setShowBorder: (showBorder: boolean) => void;
  showBackButton: boolean;
  setShowBackButton: (showBackButton: boolean) => void;
  showHistoryButton: boolean;
  setShowHistoryButton: (showHistoryButton: boolean) => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export const useHeader = () => {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};

export const useTransparentHeader = (isTransparentValue: boolean) => {
  const { isTransparent, setIsTransparent } = useHeader();

  useEffect(() => {
    setIsTransparent(isTransparentValue);
  }, [setIsTransparent, isTransparentValue]);

  return isTransparent;
};

export const useBackButton = (showBackButtonValue: boolean) => {
  const { showBackButton, setShowBackButton } = useHeader();

  useEffect(() => {
    setShowBackButton(showBackButtonValue);
  }, [setShowBackButton, showBackButtonValue]);

  return showBackButton;
};

interface HeaderProviderProps {
  children: ReactNode;
}

export const HeaderProvider: React.FC<HeaderProviderProps> = ({ children }) => {
  const pathname = usePathname();
  const { isWorkspace, workspaceId } = useChatMode();
  const { workspaces } = useWorkspace();
  const { t } = useTranslation();
  const [title, setTitle] = useState<string | ReactNode>('');
  const [isTransparent, setIsTransparent] = useState(false);
  const [rightContent, setRightContent] = useState<ReactNode | null>(null);
  const [showBorder, setShowBorder] = useState(false);
  const [showBackButton, setShowBackButtonState] = useState(false);
  const [showHistoryButton, setShowHistoryButton] = useState(true);

  // 返回按钮实际调用 router.back()（浏览器 history.back()）；如果当前标签页没有可回退的历史记录
  // （比如直接粘贴链接打开、外部跳转进来的第一个页面），点击返回没有意义，甚至可能跳出整个站点。
  // 所有调用方都是通过 useHeader() 拿到这个 setter，在这里统一拦截，不需要每个页面自己判断。
  const setShowBackButton = useCallback((value: boolean) => {
    if (value && typeof window !== 'undefined' && window.history.length <= 1) {
      setShowBackButtonState(false);
      return;
    }
    setShowBackButtonState(value);
  }, []);

  // 根据路由动态设置标题
  const getDefaultTitle = useCallback(() => {
    if (pathname === '/') {
      return '';
    }
    if (pathname === '/predic/events') {
      return t('Events & Signals');
    }

    if (pathname === '/predic/subscription') {
      return t('Orders');
    }

    if (pathname === '/predic/portfolio') {
      return t('Portfolio');
    }

    if (pathname === '/predic/portfolio/tracking') {
      return t('Track Wallet');
    }

    if (pathname === '/predic/discovery') {
      return t('Discovery');
    }

    if (pathname === '/predic/crypto') {
      return t('Crypto Market');
    }

    if (pathname.startsWith('/predic/credit-center')) {
      return t('Credit Center');
    }

    if (pathname.startsWith('/predic/r/') || pathname.includes('/predic/c/')) {
      return ''; //t('Predictions');
    }

    if (pathname.startsWith('/predic')) {
      return '';
    }

    if (pathname.includes('/c/')) {
      return 'Pythra Research';
    }
    if (pathname.includes('/r/')) {
      return t('Deep Research');
    }
    if (pathname.includes('/live-data')) {
      return 'Live Data';
    }
    if (pathname.includes('/crypto-twitter')) {
      return 'Crypto Twitter';
    }

    if (
      pathname.includes('/twitter') ||
      pathname.includes('/workspace') ||
      pathname.includes('/events')
    ) {
      return null; // 页面自己管理标题
    }
    if (pathname.includes('/search')) {
      return t('Search');
    }
    if (pathname.includes('/project')) {
      return 'Projects';
    }
    return '';
  }, [pathname, t]);

  const workspace = workspaces.find((w) => w.id === workspaceId);

  useEffect(() => {
    if (workspace && isWorkspace) {
      setTitle(`${workspace.emoji ?? ''} ${workspace.name}`);
    }
  }, [workspace, isWorkspace]);
  // 当路由改变时，更新默认标题
  useEffect(() => {
    const defaultTitle = getDefaultTitle();
    if (defaultTitle !== null) {
      setTitle(defaultTitle);
    }
  }, [pathname, setTitle, getDefaultTitle]);

  useEffect(() => {
    if (isWorkspace) {
      setRightContent(<WorkspaceSetting />);
    }
  }, [isWorkspace]);

  return (
    <HeaderContext.Provider
      value={{
        title,
        setTitle,
        isTransparent,
        setIsTransparent,
        rightContent,
        setRightContent,
        showBorder,
        setShowBorder,
        showBackButton,
        setShowBackButton,
        showHistoryButton,
        setShowHistoryButton,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
};
